import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

/** The default scrolling speed of the marquee, in pixels per second. */
const DEFAULT_MARQUEE_SPEED = 60;

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * When the `marquee` attribute is present, the slides are laid out on a single
 * auto-width track that scrolls continuously instead of fading one by one.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement} [track]
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  /**
   * The element holding the original slides, repeated to fill the marquee track.
   * @type {HTMLElement|undefined}
   */
  #marqueeGroup = undefined;

  connectedCallback() {
    super.connectedCallback();

    if (this.marquee) {
      this.#setupMarquee();
      return;
    }

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.play();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.suspend();
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    window.removeEventListener('resize', this.#handleResize);
  }

  get marquee() {
    return this.hasAttribute('marquee');
  }

  /**
   * The marquee speed in pixels per second.
   */
  get marqueeSpeed() {
    const speed = parseFloat(`${this.dataset.marqueeSpeed}`);

    return Number.isNaN(speed) || speed <= 0 ? DEFAULT_MARQUEE_SPEED : speed;
  }

  /**
   * Groups the slides together so the group can be repeated across the track.
   */
  #setupMarquee() {
    const { track } = this.refs;

    if (!track || !track.children.length) return;

    const group = document.createElement('div');
    group.className = 'announcement-bar__marquee-group';
    group.append(...track.children);
    track.appendChild(group);

    this.#marqueeGroup = group;

    this.#renderMarquee();

    // Web fonts change the width of the slides, so measure again once they land.
    document.fonts?.ready.then(() => {
      if (this.isConnected) this.#renderMarquee();
    });

    window.addEventListener('resize', this.#handleResize);
  }

  /**
   * Repeats the slides until the track is wide enough to scroll seamlessly, and
   * matches the animation duration to the configured speed.
   */
  #renderMarquee() {
    const { track } = this.refs;
    const group = this.#marqueeGroup;

    if (!track || !group) return;

    for (const clone of track.querySelectorAll('[data-marquee-clone]')) clone.remove();

    const groupWidth = group.getBoundingClientRect().width;

    if (!groupWidth) return;

    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    const distance = groupWidth + gap;

    // One copy to loop against, plus as many as it takes to cover the visible width.
    const copies = Math.ceil(this.clientWidth / distance) + 1;

    for (let i = 0; i < copies; i++) {
      track.appendChild(this.#createMarqueeClone(group));
    }

    track.style.setProperty('--announcement-marquee-distance', `${distance}px`);
    track.style.setProperty('--announcement-marquee-duration', `${distance / this.marqueeSpeed}s`);

    this.setAttribute('marquee-ready', '');
  }

  /**
   * Creates a decorative copy of the slides, hidden from assistive technology.
   * @param {HTMLElement} group - The group of slides to copy.
   */
  #createMarqueeClone(group) {
    const clone = /** @type {HTMLElement} */ (group.cloneNode(true));

    clone.setAttribute('aria-hidden', 'true');
    clone.setAttribute('data-marquee-clone', '');

    for (const element of clone.querySelectorAll('[ref]')) element.removeAttribute('ref');
    for (const element of clone.querySelectorAll('a')) element.setAttribute('tabindex', '-1');

    return clone;
  }

  #handleResize = debounce(() => this.#renderMarquee(), 250);

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    this.#current = current;

    let relativeIndex = current % (this.refs.slides ?? []).length;
    if (relativeIndex < 0) {
      relativeIndex += (this.refs.slides ?? []).length;
    }

    this.refs.slides?.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${index !== relativeIndex}`);
    });
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
